import { useState } from "react";
import { useDialogKeyboard } from '../../../hooks/useDialogKeyboard';
import {
  Box,
  Button,
  Modal,
  ModalClose,
  Sheet,
  Typography,
} from "@mui/joy";
import WarningIcon from "@mui/icons-material/Warning";

const DeleteModelModal = ({ open, onClose, model, onConfirmDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!model) return;
    
    setIsDeleting(true);
    const result = await onConfirmDelete(model.id);
    setIsDeleting(false);
    
    if (result.success) {
      onClose();
    }
  };

  useDialogKeyboard(open, { onEnter: handleDelete, onEscape: onClose, disabled: isDeleting });

  if (!model) return null;

  return (
    <Modal
      aria-labelledby="delete-model-modal-title"
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{ maxWidth: 400, width: "100%", borderRadius: "md", p: 3, boxShadow: "lg", bgcolor: "background.surface" }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} onClick={onClose} />
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <WarningIcon color="warning" />
          <Typography
            component="h2"
            id="delete-model-modal-title"
            level="h4"
            textColor="inherit"
            sx={{ fontWeight: "lg" }}
          >
            Delete Model
          </Typography>
        </Box>

        <Typography textColor="text.secondary" sx={{ mb: 2 }}>
          Are you sure you want to delete the model{" "}
          <strong>{model.display_name || model.name}</strong>?
        </Typography>

        {model.is_default && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "warning.softBg",
              borderRadius: 1,
            }}
          >
            <Typography level="body-sm" color="warning">
              This is currently the default model. Deleting it will require
              setting a new default.
            </Typography>
          </Box>
        )}

        <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
          This action cannot be undone. Any agents using this model may need to
          be reconfigured.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose} variant="plain">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="solid"
            color="danger"
            loading={isDeleting}
          >
            Delete Model
          </Button>
        </Box>
      </Sheet>
    </Modal>
  );
};

export default DeleteModelModal;
